import { AdVideo } from "../../browser-entry";
import type { AdVideoConfig } from "../../types/config";
import type { AdVideoHandle } from "../../video-ads";

/** The value supplied to the live library's legacy completion callback. */
export interface UsaTodayVideoCompletion {
  hasErrors: boolean;
}

/**
 * Public configuration accepted by the legacy USA Today they-sell bundle
 * (games.usatoday.com).
 *
 * The current renderer also accepts its modern configuration options. The
 * callback-only fields below are retained so hosts can migrate without
 * changing their bootstrap code.
 */
export interface UsaTodayVideoConfig
  extends Omit<AdVideoConfig, "finalCallback"> {
  finalCallback?: (completion: UsaTodayVideoCompletion) => void;
  onAdError?: (message: string) => void;
  onRewardReady?: () => void;
}

function toRuntimeConfig(
  config: UsaTodayVideoConfig,
  hasErrors: () => boolean,
): AdVideoConfig {
  const { finalCallback, ...videoConfig } = config;
  return {
    ...videoConfig,
    finalCallback: () => finalCallback?.({ hasErrors: hasErrors() }),
  };
}

/**
 * Compatibility facade for the live games.usatoday.com video bundle.
 *
 * The retired bundle mounted a `video-ad-component` below the supplied host
 * and removed the host id while it was active. The modern Arena host scopes
 * every preroll/CTA style to `#ark_pre-roll` descendants, so the host id must
 * be preserved or the CTA screen renders unstyled.
 */
export class UsaTodayAdVideo extends AdVideo {
  private element?: HTMLElement;
  private handle?: AdVideoHandle;
  private parent?: HTMLElement;
  private hasErrors = false;

  static preload(): void {
    // The live library deliberately exposed preload as a no-op.
  }

  render(config: UsaTodayVideoConfig = {}): AdVideoHandle {
    this.removePreviousElement();
    this.hasErrors = false;

    const parent = typeof config.element === "string"
      ? document.getElementById(config.element)
      : null;
    if (!parent) {
      const handle = super.render(toRuntimeConfig(config, () => this.hasErrors));
      this.handle = handle;
      return handle;
    }

    const element = document.createElement("video-ad-component");
    this.element = element;
    this.parent = parent;

    // Preserve the host id: Arena 5.1 styles the preroll exclusively through
    // `#ark_pre-roll` descendant selectors (`.ctaContainer`, `.ctaButton`, …).
    parent.appendChild(element);

    const handle = super.render({
      ...toRuntimeConfig(config, () => this.hasErrors),
      element,
    });
    this.handle = handle;

    void handle.finished.then((outcome) => {
      if (outcome.reason !== "failed") return;
      this.hasErrors = true;
      config.onAdError?.(
        outcome.error?.message ?? "Unexpected video-ad rendering error",
      );
    });
    return handle;
  }

  showReward(): void {
    // Rewarded inventory is not implemented by the modern video runtime yet.
    // Preserve the legacy no-throw imperative surface for partner bootstraps.
  }

  protected applyArenaShim(): void {
    // The legacy custom-element host owns this layout; do not add the modern
    // Arena/XML wrapper around it.
  }

  private removePreviousElement(): void {
    this.handle?.destroy();
    this.handle = undefined;

    const { element, parent } = this;
    if (element && parent && element.parentNode === parent) {
      parent.removeChild(element);
    }

    this.element = undefined;
    this.parent = undefined;
  }
}
